import VueRouter, { Route } from 'vue-router';
import * as Sentry from '@sentry/browser';
import HomePage from '@/views/HomePage.vue';
import * as NProgress from 'nprogress';

export function setFavIcon(hasCustomFavicon: boolean, tenantId: string) {
    const prevFavIcon = document.getElementById('favicon');
    if (prevFavIcon) {
        document.head.removeChild(prevFavIcon);
    }
    let iconUrl = `${process.env.VUE_APP_BASE_API_URL}favicon.ico`;
    if (hasCustomFavicon) {
        iconUrl = `${process.env.VUE_APP_BASE_API_URL}img/branding/${tenantId}/icon-app-192x192.png`;
    }
    const link = document.createElement('link');
    link.setAttribute('id', 'favicon');
    link.setAttribute('rel', 'icon');
    link.setAttribute('href', iconUrl);
    document.head.appendChild(link);
}

export function beforeEnterVerify(to: Route, from: Route, next: any) {
    const store = require('@/store').default;
    if (store.state.session.portalAccessToken && (!to.query.refNo || to.query.refNo === store.state.session.refNo)) {
        return next({
            name: 'GrievancePage',
            params: { instanceId: to.params.instanceId, refNo: store.state.session.refNo },
        });
    }
    next();
}

export function beforeEnterConfirm(to: Route, from: Route, next: any) {
    const store = require('@/store').default;
    const refNo = store.state.confirm.refNo;
    if (refNo) {
        if (refNo === to.params.refNo) {
            return next();
        }
        return next({ name: 'ConfirmPage', params: { instanceId: to.params.instanceId, refNo } });
    }
    next({ name: 'CreateGrievance', params: { instanceId: to.params.instanceId } });
}

export async function beforeEnterGrievance(to: Route, from: Route, next: any) {
    const store = require('@/store').default;
    const validateRoute = () => {
        if (to.params.refNo === store.state.session.refNo) {
            return next();
        } else {
            return next({
                name: 'VerifyPage',
                params: { instanceId: to.params.instanceId },
                query: { refNo: to.params.refNo },
            });
        }
    };
    if (store.state.session.portalAccessToken) {
        validateRoute();
    } else {
        next({
            name: 'VerifyPage',
            params: { instanceId: to.params.instanceId },
            query: { refNo: to.params.refNo },
        });
    }
}

const router = new VueRouter({
    base: process.env.VUE_APP_ROUTER_BASE_URL,
    mode: 'history',
    routes: [
        {
            path: '/:instanceId',
            name: 'HomePage',
            component: HomePage,
        },
        {
            path: '/:instanceId/verify',
            name: 'VerifyPage',
            component: () => import(/* webpackChunkName: "verify" */ '../views/VerifyPage.vue'),
            beforeEnter: beforeEnterVerify,
        },
        {
            path: '/:instanceId/grievance',
            redirect: to => `/${to.params.instanceId}/grievance/create`,
        },
        {
            path: '/:instanceId/grievance/create',
            name: 'CreateGrievance',
            component: () => import(/* webpackChunkName: "create_grievance" */ '../views/CreateGrievancePage.vue'),
        },
        {
            path: '/:instanceId/confirm/:refNo',
            name: 'ConfirmPage',
            component: () => import(/* webpackChunkName: "confirm" */ '../views/ConfirmPage.vue'),
            beforeEnter: beforeEnterConfirm,
        },
        {
            path: '/:instanceId/grievance/:refNo',
            name: 'GrievancePage',
            component: () => import(/* webpackChunkName: "grievance" */ '../views/GrievancePage.vue'),
            beforeEnter: beforeEnterGrievance,
            children: [
                {
                    path: 'follow_up',
                    name: 'FollowUpPage',
                },
            ],
        },
        {
            path: '/:instanceId/forgot-password',
            name: 'ForgotPasswordPage',
            component: () => import(/* webpackChunkName: "forgot_password" */ '../views/ForgotPasswordPage.vue'),
        },
        {
            path: '/:instanceId/reset-password',
            name: 'ResetPasswordPage',
            component: () => import(/* webpackChunkName: "reset_password" */ '../views/ResetPasswordPage.vue'),
        },
        {
            path: '*',
            name: 'NotFound',
            component: () => import(/* webpackChunkName: "not_found" */ '../views/NotFoundPage.vue'),
        },
    ],
});

export async function globalBeforeEach(to: Route, from: Route, next: any) {
    NProgress.start();
    if (to.name === 'NotFound') {
        return next();
    }

    const store = require('@/store').default;
    const { instanceId } = to.params;
    if (store.state.instanceId === instanceId) {
        return next();
    }

    try {
        await store.dispatch('initPortal', { instanceId });
        setFavIcon(store.state.hasCustomFavIcon, store.state.tenantId);
        try {
            await store.dispatch('session/refresh');
            // eslint-disable-next-line no-empty
        } catch (err) {}
        return next();
    } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
        Sentry.captureException(err);
        return next({ name: 'NotFound' });
    }
}

export function globalAfterEach() {
    NProgress.done();
}

router.beforeEach(globalBeforeEach);
router.afterEach(globalAfterEach);

export default router;
